export const DATA_ATTR_NAME_START_PAGE = "data-start-page";

/** Scrolling composable for chat-like interfaces. Namely sticky bottom scrolling
 * for long streamed messages, and "next page" scrolling for new messages to improve
 * readability for shorter messages.
 */
export const useSmartScroll = ({ bottomMargin = 100 } = {}) => {
  const pageRef = ref(null);
  const spacerRef = ref(null);
  const messagesRef = ref([]);

  // Actual empty space calculated once at the start of each page.
  const maxSpacePx = ref(0);
  const shouldStick = ref(true);

  // Scroll listener to detect user's explicit intent to stick/unstick from bottom.
  const updateSticky = () => {
    if (!pageRef.value) return;
    const { scrollHeight, scrollTop, clientHeight } = pageRef.value;
    shouldStick.value = scrollHeight - scrollTop - clientHeight < bottomMargin;
    // console.log(shouldStick.value);
  };

  const scrollBottom = (force = false) => {
    requestAnimationFrame(() => {
      if (!pageRef.value) return;
      // Need to somehow abort the scroll...
      if (!force && !shouldStick.value) return;
      pageRef.value.scrollTop = pageRef.value.scrollHeight;
    });
  };

  const setSpacerHeight = (px) => {
    requestAnimationFrame(() => {
      if (!spacerRef.value) return;
      spacerRef.value.style.height = `${px}px`;
    });
  };

  // Trigger stuff when the current streaming message size changes.
  const msgSizeObserver = new ResizeObserver((entries) => {
    const page = pageRef.value;
    const curMsgElem = entries[0].target;
    if (!page) return;

    if (curMsgElem.getAttribute(DATA_ATTR_NAME_START_PAGE)) {
      // Calculate empty space for spacer only at start of page to subtract user's message.
      // Assumes user message already has a concrete height.
      maxSpacePx.value = pageRef.value.offsetHeight - curMsgElem.offsetHeight;
      setSpacerHeight(Math.max(maxSpacePx.value, 0));
      scrollBottom(true);
    } else {
      const curEmptyHeight = maxSpacePx.value - curMsgElem.offsetHeight;
      setSpacerHeight(Math.max(curEmptyHeight, 0));
      if (curEmptyHeight <= 0 && shouldStick.value) scrollBottom();
    }
  });

  // Note that Vue's watch doesn't re-fire if no change in object identity.
  watch(
    pageRef,
    (page, _, onCleanup) => {
      if (!page) return;
      // Scroll to bottom when new page is set (which only happens once).
      scrollBottom();
      // Turns out it is really non-trivial to detect if scrolling was initiated
      // by the user, especially when using smooth scroll.
      // If tall images become a problem, someone will have to bite the bullet and
      // write the complicated code required to actually discern user scroll from
      // programmatic scroll (namely, how to detect a user scroll interrupting a
      // programmatic smooth scroll...).
      page.addEventListener("scroll", updateSticky, { passive: true });

      onCleanup(() => {
        page.removeEventListener("scroll", updateSticky);
      });
    },
    { immediate: true }
  );

  // Reset space height when set (also should only happen once).
  watch(spacerRef, () => setSpacerHeight(0), { immediate: true });

  // Attach the msgSizeObserver to the newest/current streaming message.
  watch(
    () => messagesRef.value.at(-1),
    (curMsgElem, _, onCleanup) => {
      if (!curMsgElem) return;
      msgSizeObserver.observe(curMsgElem);

      onCleanup(() => msgSizeObserver.disconnect());
    },
    { immediate: true }
  );

  return {
    pageRef,
    spacerRef,
    messagesRef,
  };
};
